import React, {useRef, useState} from 'react'
import {Link as RouterLink, useLocation} from 'react-router-dom'
import {Item, SidenavBox, SubItems} from './styles'
import DashboardIcon from '../../../images/icons/new-dashboard/dashboard-icon.svg'
import ResourcesIcon from '../../../images/icons/new-dashboard/resources-icon.svg'
import GroupIcon from '../../../images/icons/new-dashboard/group-icon.svg'
import ReferralIcon from '../../../images/icons/new-dashboard/referral-icon.svg'
import SoftwareIcon from '../../../images/icons/new-dashboard/software-icon.svg'
import ARELive from '../../../images/icons/new-dashboard/are-live.svg'
import ARECommunity from '../../../images/icons/new-dashboard/are-community.svg'
import ChallenteAnimation from '../../../images/30-day-logo.json'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import {LinkBox, LinkBoxExternal} from '../../../styles/global'
import {useSelector} from 'react-redux'
import {
  selectMember,
  selectMemberAccess,
  trackReferralsProgramClick,
} from '../../../redux/members'
import {useAuth} from '../../../components/AuthProvider'
import {ChallengeBox} from '../../../components/SideNav/styles'
import Lottie from 'react-lottie'
import {Typography} from '@mui/material'
import {Button} from '../../../components/button'
import {selectActiveChallenge} from '../../../redux/challenges'
import {useAppDispatch} from '../../../redux/configureStore'

type Props = {
  onClose?: () => void
}

const View = ({onClose}: Props) => {
  const location = useLocation()
  const auth = useAuth()

  const dispatch = useAppDispatch()

  const isGroupRoute = location.pathname.includes('/group/')

  const [isResourcesExpanded, setIsResourcesExpanded] = useState(true)
  const [isGroupExpanded, setIsGroupExpanded] = useState(isGroupRoute)
  const resourcesRef = useRef<HTMLDivElement | null>(null)
  const groupRef = useRef<HTMLDivElement | null>(null)

  const access = useSelector(selectMemberAccess)
  const member = useSelector(selectMember)
  const challenge = useSelector(selectActiveChallenge)

  const isPremiumGroupMember =
    member &&
    member.accessLevel === 'premium' &&
    member.groupAccounts &&
    member.groupAccounts.length > 0

  const softwareSubscription =
    access && Object.keys(access).length !== 0
      ? Number(access.software?.videos?.value) === 1
      : false

  return (
    <SidenavBox>
      <LinkBox component={RouterLink} to={'/'} onClick={onClose}>
        <Item>
          <img className="itemIcon" src={DashboardIcon} alt="Dashboard" />
          Dashboard
        </Item>
      </LinkBox>
      {member && member.isGroupAdmin && (
        <>
          <Item onClick={() => setIsGroupExpanded(!isGroupExpanded)}>
            <img className="itemIcon" src={GroupIcon} alt="Group Membership" />
            Group Membership
            {isGroupExpanded ? (
              <ExpandLessIcon fontSize="small" />
            ) : (
              <ExpandMoreIcon fontSize="small" />
            )}
          </Item>
          <SubItems
            $isExpanded={isGroupExpanded}
            height={groupRef.current?.scrollHeight}
            ref={groupRef}
          >
            <LinkBox
              component={RouterLink}
              to={'/group/manage'}
              onClick={onClose}
            >
              <Item $isSubItem={true}>
                <span>•</span> Manage Group
              </Item>
            </LinkBox>
            {!isPremiumGroupMember && (
              <LinkBox
                component={RouterLink}
                to={'/group/reporting'}
                onClick={onClose}
              >
                <Item $isSubItem={true}>
                  <span>•</span> Group Reporting
                </Item>
              </LinkBox>
            )}
            <LinkBox
              component={RouterLink}
              to={'/group/billing'}
              onClick={onClose}
            >
              <Item $isSubItem={true}>
                <span>•</span> Group Billing
              </Item>
            </LinkBox>
          </SubItems>
        </>
      )}

      <Item onClick={() => setIsResourcesExpanded(!isResourcesExpanded)}>
        <img className="itemIcon" src={ResourcesIcon} alt="Resources" />
        Resources
        {isResourcesExpanded ? (
          <ExpandLessIcon fontSize="small" />
        ) : (
          <ExpandMoreIcon fontSize="small" />
        )}
      </Item>
      <SubItems
        $isExpanded={isResourcesExpanded}
        height={resourcesRef.current?.scrollHeight}
        ref={resourcesRef}
      >
        <LinkBoxExternal
          href="https://www.blackspectacles.com/podcast"
          target="_blank"
        >
          <Item $isSubItem={true}>
            <span>•</span> <img src={ARELive} alt="ARE Live" />
          </Item>
        </LinkBoxExternal>
        <LinkBoxExternal
          href="https://community.blackspectacles.com/"
          target="_blank"
        >
          <Item $isSubItem={true}>
            <span>•</span> <img src={ARECommunity} alt="ARE Community" />
          </Item>
        </LinkBoxExternal>
        <LinkBoxExternal
          href="https://www.blackspectacles.com/blog"
          target="_blank"
        >
          <Item $isSubItem={true}>
            <span>•</span> Blog
          </Item>
        </LinkBoxExternal>
        <LinkBox
          component={RouterLink}
          to={'/virtual-workshops'}
          onClick={onClose}
        >
          <Item $isSubItem={true}>
            <span>•</span> Virtual Workshops
          </Item>
        </LinkBox>
      </SubItems>

      <LinkBoxExternal
        href="https://www.blackspectacles.com/referral"
        target="_blank"
        onClick={() => {
          if (auth.isAuthenticated) {
            dispatch(trackReferralsProgramClick({auth}))
          }
        }}
      >
        <Item>
          <img className="itemIcon" src={ReferralIcon} alt="Referral Program" />
          Referral Program
        </Item>
      </LinkBoxExternal>
      {softwareSubscription && (
        <LinkBox
          component={RouterLink}
          to={'/profile/dashboard/software/topics'}
          onClick={onClose}
        >
          <Item>
            <img className="itemIcon" src={SoftwareIcon} alt="Software" />
            Software
          </Item>
        </LinkBox>
      )}
      <>
        {auth.isAuthenticated &&
          !isPremiumGroupMember &&
          challenge?.hasEntry && (
            <ChallengeBox $isActive={true}>
              <Lottie
                options={{
                  loop: true,
                  autoplay: true,
                  animationData: ChallenteAnimation,
                }}
              />
              <Typography variant="body2">
                Start studying today and connect with others studying the same
                division
              </Typography>
              <Button
                color="secondary"
                children="Join the Community Now"
                id="challenge-button"
                onClick={() =>
                  window.open(
                    'https://community.blackspectacles.com/t/join-the-30-day-study-challenge-may-edition/6786',
                  )
                }
              />
            </ChallengeBox>
          )}
      </>
    </SidenavBox>
  )
}

export default View
